import { NestFactory } from '@nestjs/core';
import {
  FastifyAdapter,
  type NestFastifyApplication,
} from '@nestjs/platform-fastify';
import { fastifyMultipart } from '@fastify/multipart';
import { ValidationPipe } from '@nestjs/common';
import { useContainer } from 'class-validator';
import {
  SwaggerModule,
  DocumentBuilder,
  type SwaggerDocumentOptions,
  type SwaggerCustomOptions,
} from '@nestjs/swagger';
import { apiReference } from '@scalar/nestjs-api-reference';
// import { NestExpressApplication } from '@nestjs/platform-express';
import { join } from 'path';
import Handlebars from 'handlebars';
import { AppModule } from './app.module';

async function bootstrap() {
  const app = await NestFactory.create<NestFastifyApplication>(
    AppModule,
    new FastifyAdapter({
      logger: true,
    }),
  );

  await app.register(fastifyMultipart);

  app.enableCors({
    origin: '*',
    exposedHeaders: [],
  });

  app.useStaticAssets({
    root: join(__dirname, '..', 'public'),
    prefix: '/public/',
  });

  app.setViewEngine({
    engine: {
      handlebars: Handlebars,
    },
    templates: join(__dirname, '..', 'views'),
  });

  app.useGlobalPipes(
    new ValidationPipe({
      transform: true,
      whitelist: true,
    }),
  );

  useContainer(app.select(AppModule), { fallbackOnErrors: true });

  const config = new DocumentBuilder()
    .setTitle(`${process.env.APP_NAME} API`)
    .setDescription(`This is the api documentation of ${process.env.APP_NAME}`)
    .addBearerAuth()
    .addSecurityRequirements('bearer')
    .addGlobalResponse({
      status: 400,
      schema: {
        properties: {
          success: {
            type: 'boolean',
            example: false,
          },
          statusCode: {
            type: 'number',
            example: 400,
          },
          message: {
            type: 'string',
            example: 'Validation failed',
          },
          errorType: {
            type: 'string',
            example: 'BAD_REQUEST_ERROR',
          },
          errors: {
            type: 'array',
            example: ['Error'],
          },
        },
      },
    })
    .build();
  const swaggerDocOptions: SwaggerDocumentOptions = {
    autoTagControllers: true,
  };
  const swaggerCustomOptions: SwaggerCustomOptions = {
    jsonDocumentUrl: 'docs-json',
    yamlDocumentUrl: 'docs-yaml',
    swaggerOptions: {
      persistAuthorization: true,
    },
  };
  const documentFactory = () =>
    SwaggerModule.createDocument(app, config, swaggerDocOptions);
  SwaggerModule.setup('swagger', app, documentFactory, swaggerCustomOptions);

  app.use(
    '/docs',
    apiReference({
      content: documentFactory,
      withFastify: true,
      persistAuth: true,
      authentication: {
        preferredSecurityScheme: 'bearer',
      },
      metaData: {
        title: `${process.env.APP_NAME} API`,
      },
    }),
  );

  await app.listen(process.env.PORT!, process.env.HOST!);

  console.log(`Application is running on: ${await app.getUrl()}`);
}
bootstrap().catch((err) => {
  console.error(err);
});
